"use client"

import React, { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import Link from "next/link"
import { LucideIcon, X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import Image from "next/image"
import { LOGO } from "@/constants/images"
import { useTheme } from "next-themes"

interface NavItem {
  name: string
  url: string
  icon?: LucideIcon
  children?: NavItem[]
  isMegaMenu?: boolean
  flagUrl?: string
}

interface MobileSidePanelProps {
  items: NavItem[]
  isOpen: boolean
  onClose: () => void
  activeTab: string
  setActiveTab: (name: string) => void
  topOffset?: number
}

interface HistoryItem {
  level: number
  title: string
  items: NavItem[]
  scrollPosition?: number
}

export function MobileSidePanel({
  items,
  isOpen,
  onClose,
  activeTab,
  setActiveTab,
  topOffset = 0,
}: MobileSidePanelProps) {
  const { theme } = useTheme()
  const [currentLevel, setCurrentLevel] = useState(0)
  const [historyStack, setHistoryStack] = useState<HistoryItem[]>([])
  const [currentTitle, setCurrentTitle] = useState("Menu")
  const [displayedItems, setDisplayedItems] = useState<NavItem[]>(items)
  const panelContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      setDisplayedItems(items)
      setCurrentLevel(0)
      setHistoryStack([])
      setCurrentTitle("Menu")
    } else {
      setTimeout(() => {
        setDisplayedItems([])
      }, 300)
    }
  }, [isOpen, items])

  const navigateToLevel = (newItems: NavItem[], title: string, newLevel: number) => {
    if (panelContentRef.current) {
      const currentScrollPosition = panelContentRef.current.scrollTop
      setHistoryStack(prev => [...prev, { level: currentLevel, title: currentTitle, items: displayedItems, scrollPosition: currentScrollPosition }])
    }
    setDisplayedItems(newItems)
    setCurrentTitle(title)
    setCurrentLevel(newLevel)
    if (panelContentRef.current) {
      panelContentRef.current.scrollTop = 0
    }
  }

  const goBack = () => {
    const lastState = historyStack.pop()
    if (lastState) {
      setDisplayedItems(lastState.items)
      setCurrentTitle(lastState.title)
      setCurrentLevel(lastState.level)
      setHistoryStack([...historyStack])
      if (panelContentRef.current && lastState.scrollPosition !== undefined) {
        setTimeout(() => {
          if (panelContentRef.current) {
            panelContentRef.current.scrollTop = lastState.scrollPosition || 0
          }
        }, 50)
      }
    }
  }

  const handleItemClick = (item: NavItem) => {
    if (item.name === "StudyGuide" && item.children && item.children.length > 0 && currentLevel === 0) {
      navigateToLevel(item.children, "StudyGuide", 1)
    } else if (currentLevel === 1 && item.children && item.children.length > 0) {
      navigateToLevel(item.children, item.name, 2)
    } else {
      setActiveTab(item.name)
      onClose()
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
          />
          
          {/* Side Panel */}
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed left-0 w-[280px] bg-background/5 backdrop-blur-lg z-50 flex flex-col"
            style={{ 
              top: `${topOffset}px`, 
              height: `calc(100vh - ${topOffset}px)` 
            }}
          >
            {/* Navigation Items */}
            <nav ref={panelContentRef} className="flex-grow p-4 space-y-1 overflow-y-auto">
              {currentLevel === 2 && (!displayedItems || displayedItems.length === 0) ? (
                <div className="p-6 text-sm text-muted-foreground flex flex-col items-center justify-center h-full space-y-3 text-center">
                  <Sparkles className="w-12 h-12 text-primary/70 mb-2" />
                  <p className="font-semibold text-lg text-foreground/90 text-center">New Adventures Unfolding!</p>
                  <p className="text-sm text-muted-foreground">
                    We&apos;re charting new territories. More amazing destinations coming soon!
                  </p>
                </div>
              ) : (
                displayedItems.map((item) => {
                  const isExpandableAtCurrentLevel = 
                    (item.name === "StudyGuide" && currentLevel === 0 && item.children && item.children.length > 0) ||
                    (currentLevel === 1 && item.children && item.children.length > 0)

                  const Icon = item.icon || (isExpandableAtCurrentLevel ? ChevronRight : undefined)
                  const isActive = activeTab === item.name && currentLevel === 0

                  if (isExpandableAtCurrentLevel) {
                    return (
                      <div
                        key={item.name}
                        onClick={() => handleItemClick(item)}
                        className={cn(
                          "relative flex items-center gap-3 px-4 py-3 rounded-lg transition-colors cursor-pointer",
                          "text-foreground/80 hover:text-primary hover:bg-primary/5",
                        )}
                      >
                        {item.flagUrl && currentLevel === 1 && (
                          <Image src={item.flagUrl} alt={`${item.name} flag`} width={24} height={16} className="rounded-sm" />
                        )}
                        {item.icon && (!item.flagUrl || currentLevel !==1) && <item.icon size={18} strokeWidth={2.0} /> }
                        {!item.icon && Icon && (!item.flagUrl || currentLevel !==1) && <Icon size={18} strokeWidth={2.0} /> }
                        <span className="font-medium flex-grow truncate">{item.name}</span>
                        <ChevronRight size={18} className="text-foreground/60" />
                      </div>
                    )
                  }

                  return (
                    <Link
                      key={item.name}
                      href={item.url}
                      onClick={() => {
                        setActiveTab(item.name)
                        onClose()
                      }}
                      className={cn(
                        "relative flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
                        "text-foreground/80 hover:text-primary hover:bg-primary/5",
                        isActive && "text-primary bg-primary/5"
                      )}
                    >
                      {Icon && <Icon size={18} strokeWidth={isActive ? 2.5 : 2.0} />}
                      <span className="font-medium flex-grow truncate">{item.name}</span>
                      {isActive && (
                        <motion.div
                          layoutId="mobile-side-active"
                          className="absolute inset-0 bg-primary/5 rounded-lg -z-10"
                          initial={false}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 30,
                          }}
                        />
                      )}
                    </Link>
                  )
                })
              )}
            </nav>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
} 